import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [salon, setSalon] = useState({ status: "none" }); // none | pending | approved | rejected

  const refreshSalon = useCallback(async () => {
    try {
      const { data } = await api.get("/salon/me");
      setSalon(data || { status: "none" });
      // Fire a fire-and-forget visit ping for approved partners (analytics)
      if (data?.status === "approved") {
        api.post("/salon/ping").catch(() => {});
      }
    } catch (e) {
      setSalon({ status: "none" });
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      await refreshSalon();
    } catch (e) {
      setUser(null);
      setSalon({ status: "none" });
    } finally {
      setLoading(false);
    }
  }, [refreshSalon]);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip /me check.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // ignore
    }
    setUser(null);
    setSalon({ status: "none" });
  };

  return (
    <AuthContext.Provider value={{ user, loading, salon, checkAuth, refreshSalon, loginWithGoogle, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
